/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════
// Imports.js — Wizard d'import CSV (Batch 4)
// 4 étapes : Upload → Mapping → Preview → Run/Done
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';
const api = axios.create({ baseURL: API });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('lexback_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// Tokens dark cohérents avec Admin.js
const D = {
  bg: '#13131A', s1: '#1C1C28', s2: '#252533',
  b1: '#3A3A4A', b2: 'rgba(255,255,255,0.16)',
  tx: '#EDEAE4', tx2: '#B8B8C4', tx3: '#7E7E8E',
  or: '#D4A843', green: '#2ECC8A', red: '#EF5B5B', blue: '#5B8DEF',
};

// Liste des champs Lexback mappables
const LEXBACK_FIELDS = [
  { key: 'societe_nom',              label: 'Société (nom)',           required: true },
  { key: 'societe_siren',            label: 'SIREN',                   required: false },
  { key: 'formality_type',           label: 'Type de formalité',       required: false },
  { key: 'status_label',             label: 'Statut',                  required: false },
  { key: 'greffe',                   label: 'Greffe',                  required: false },
  { key: 'cabinet',                  label: 'Cabinet BtoB',            required: false },
  { key: 'formaliste',               label: 'Formaliste',              required: false },
  { key: 'commercial',               label: 'Commercial',              required: false },
  { key: 'created_at',               label: 'Date création',           required: false },
  { key: 'email_client_final',       label: 'Email client',            required: false },
  { key: 'telephone_client_final',   label: 'Téléphone client',        required: false },
  { key: 'elements_manquants',       label: 'Éléments manquants',      required: false },
  { key: 'notes_formaliste_lexback', label: 'Notes internes',          required: false },
  { key: 'etat_facturation',         label: 'État facturation',        required: false },
];

const ATTACHMENT_CATEGORIES = [
  { value: '',              label: '— Pas un attachment —' },
  { value: 'officiel',      label: 'Officiel (Kbis, RBE, etc.)' },
  { value: 'juridique',     label: 'Juridique (Statuts, PV, etc.)' },
  { value: 'justificative', label: 'Justificative (Banque, Domiciliation)' },
];

// ─── Helpers UI ──────────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  const steps = ['Upload', 'Mapping', 'Preview', 'Import'];
  return (
    <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', alignItems: 'center' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 14px', borderRadius: '999px',
            background: i === current ? D.or : i < current ? 'rgba(212,168,67,0.15)' : 'transparent',
            border: i === current ? `1px solid ${D.or}` : `1px solid ${D.b1}`,
            color: i === current ? D.bg : i < current ? D.or : D.tx3,
            fontSize: '12px', fontWeight: i === current ? 600 : 400,
          }}>
            <span style={{
              width: '20px', height: '20px', borderRadius: '50%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: i === current ? D.bg : i < current ? D.or : 'transparent',
              color: i === current ? D.or : i < current ? D.bg : D.tx3,
              fontSize: '10px', fontWeight: 600,
              border: i < current ? 'none' : `1px solid currentColor`,
            }}>
              {i < current ? '✓' : i + 1}
            </span>
            {s}
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 0, width: '20px', height: '1px', background: D.b1 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Étape 1 : Upload ────────────────────────────────────────────────────────
function StepUpload({ workspaceId, onUploaded, workspaces, embedded = false }) {
  const [file, setFile] = useState(null);
  const [selectedWs, setSelectedWs] = useState(workspaceId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // En mode embedded, on force le workspace au mount
  useEffect(() => {
    if (embedded && workspaceId) setSelectedWs(workspaceId);
  }, [embedded, workspaceId]);

  const handleFile = (f) => {
    setError('');
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      setError('Format non supporté. Utilisez un fichier .csv');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('Fichier trop gros (max 10 MB)');
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) { setError('Sélectionne un fichier'); return; }
    if (!selectedWs) { setError('Choisis le workspace de destination'); return; }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('workspace_id', selectedWs);
      const res = await api.post('/api/admin/imports/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur upload');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', color: D.tx2, marginBottom: '6px' }}>
            Workspace de destination
          </div>
          <select
            value={selectedWs}
            onChange={e => setSelectedWs(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px',
              background: D.s2, border: `1px solid ${D.b1}`, borderRadius: '8px',
              color: D.tx, fontSize: '13px', outline: 'none',
            }}
          >
            <option value="">— Choisir un client —</option>
            {(workspaces || []).map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files[0]);
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? D.or : D.b1}`,
          borderRadius: '12px', padding: '40px 20px',
          textAlign: 'center', cursor: 'pointer',
          background: dragOver ? 'rgba(212,168,67,0.05)' : D.s1,
          transition: 'all 0.2s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          onChange={e => handleFile(e.target.files[0])}
          style={{ display: 'none' }}
        />
        {file ? (
          <div>
            <div style={{ fontSize: '14px', color: D.tx, fontWeight: 500, marginBottom: '4px' }}>
              {file.name}
            </div>
            <div style={{ fontSize: '11px', color: D.tx3 }}>
              {(file.size / 1024).toFixed(1)} KB · Cliquer pour changer
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '14px', color: D.tx, marginBottom: '6px' }}>
              Glisse un CSV ici ou clique pour sélectionner
            </div>
            <div style={{ fontSize: '11px', color: D.tx3 }}>
              Format CSV · Max 10 MB · Encodage UTF-8 ou Latin-1
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          marginTop: '12px', padding: '10px 14px',
          background: 'rgba(239,91,91,0.10)', border: `1px solid ${D.red}40`,
          borderRadius: '6px', fontSize: '12px', color: D.red,
        }}>{error}</div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleUpload}
          disabled={!file || !selectedWs || loading}
          style={{
            background: !file || !selectedWs ? D.s2 : D.or,
            color: !file || !selectedWs ? D.tx3 : D.bg,
            border: 'none', padding: '10px 24px', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600,
            cursor: !file || !selectedWs ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Upload...' : 'Continuer →'}
        </button>
      </div>
    </div>
  );
}

// ─── Étape 2 : Mapping ───────────────────────────────────────────────────────
function StepMapping({ csvImport, onMappingDone, onBack }) {
  const [mapping, setMapping] = useState(csvImport.suggested_mapping || {});
  const [attachmentCols, setAttachmentCols] = useState(
    csvImport.suggested_mapping?.attachment_columns || {}
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const detectedColumns = csvImport.detected_columns || [];

  // Inverser le mapping pour le rendu : par colonne CSV, quel champ Lexback ?
  const colToField = {};
  Object.keys(mapping).forEach(field => {
    if (field !== 'attachment_columns' && mapping[field]) {
      colToField[mapping[field]] = field;
    }
  });

  const handleFieldChange = (csvCol, lexbackField) => {
    const newMapping = { ...mapping };
    // Retirer cette colonne de tout autre champ (1 colonne ↔ 1 champ)
    Object.keys(newMapping).forEach(k => {
      if (k !== 'attachment_columns' && newMapping[k] === csvCol) {
        delete newMapping[k];
      }
    });
    if (lexbackField) {
      newMapping[lexbackField] = csvCol;
    }
    setMapping(newMapping);
    // Si on associe à un champ Lexback, retirer des attachments
    if (lexbackField) {
      const newAtt = { ...attachmentCols };
      delete newAtt[csvCol];
      setAttachmentCols(newAtt);
    }
  };

  const handleAttachmentChange = (csvCol, category) => {
    const newAtt = { ...attachmentCols };
    if (category) {
      newAtt[csvCol] = category;
    } else {
      delete newAtt[csvCol];
    }
    setAttachmentCols(newAtt);
    // Si on associe à un attachment, retirer des champs scalaires
    if (category) {
      const newMapping = { ...mapping };
      Object.keys(newMapping).forEach(k => {
        if (k !== 'attachment_columns' && newMapping[k] === csvCol) {
          delete newMapping[k];
        }
      });
      setMapping(newMapping);
    }
  };

  const handleNext = async () => {
    if (!mapping.societe_nom) {
      setError('Le champ "Société" est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const finalMapping = {
        ...mapping,
        attachment_columns: Object.keys(attachmentCols).length > 0 ? attachmentCols : undefined,
      };
      await api.patch(`/api/admin/imports/${csvImport.id}/mapping`, {
        mapping: finalMapping,
      });
      onMappingDone(finalMapping);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur sauvegarde mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '12px', fontSize: '12px', color: D.tx2 }}>
        Le système a détecté <strong>{detectedColumns.length} colonnes</strong> dans ton CSV. Vérifie le mapping ci-dessous et ajuste si nécessaire.
      </div>

      <div style={{ background: D.s1, borderRadius: '10px', overflow: 'hidden', border: `1px solid ${D.b1}` }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 200px 220px',
          padding: '10px 14px', background: D.s2,
          fontSize: '10px', textTransform: 'uppercase', color: D.tx3, fontWeight: 600, letterSpacing: '0.04em',
        }}>
          <div>Colonne CSV</div>
          <div>Champ Lexback</div>
          <div>OU Catégorie attachment</div>
        </div>
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {detectedColumns.map((col, i) => {
            const currentField = colToField[col] || '';
            const currentAtt = attachmentCols[col] || '';
            return (
              <div key={col} style={{
                display: 'grid', gridTemplateColumns: '1fr 200px 220px',
                gap: '8px', padding: '10px 14px',
                borderBottom: i < detectedColumns.length - 1 ? `0.5px solid ${D.b1}` : 'none',
                alignItems: 'center',
              }}>
                <div style={{ fontSize: '12px', color: D.tx, fontWeight: 500 }}>{col}</div>
                <select
                  value={currentField}
                  onChange={e => handleFieldChange(col, e.target.value)}
                  disabled={!!currentAtt}
                  style={{
                    padding: '5px 8px', background: D.s2,
                    border: `1px solid ${D.b1}`, borderRadius: '5px',
                    color: currentAtt ? D.tx3 : D.tx, fontSize: '11px',
                    opacity: currentAtt ? 0.4 : 1,
                  }}
                >
                  <option value="">— Ignorer —</option>
                  {LEXBACK_FIELDS.map(f => (
                    <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                  ))}
                </select>
                <select
                  value={currentAtt}
                  onChange={e => handleAttachmentChange(col, e.target.value)}
                  disabled={!!currentField}
                  style={{
                    padding: '5px 8px', background: D.s2,
                    border: `1px solid ${D.b1}`, borderRadius: '5px',
                    color: currentField ? D.tx3 : D.tx, fontSize: '11px',
                    opacity: currentField ? 0.4 : 1,
                  }}
                >
                  {ATTACHMENT_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div style={{
          marginTop: '12px', padding: '10px 14px',
          background: 'rgba(239,91,91,0.10)', border: `1px solid ${D.red}40`,
          borderRadius: '6px', fontSize: '12px', color: D.red,
        }}>{error}</div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{
          background: 'transparent', color: D.tx2,
          border: `1px solid ${D.b1}`, padding: '10px 20px', borderRadius: '8px',
          fontSize: '13px', cursor: 'pointer',
        }}>← Retour</button>
        <button
          onClick={handleNext}
          disabled={saving || !mapping.societe_nom}
          style={{
            background: !mapping.societe_nom ? D.s2 : D.or,
            color: !mapping.societe_nom ? D.tx3 : D.bg,
            border: 'none', padding: '10px 24px', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600,
            cursor: !mapping.societe_nom ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? '...' : 'Voir l\'aperçu →'}
        </button>
      </div>
    </div>
  );
}

// ─── Étape 3 : Preview ───────────────────────────────────────────────────────
function StepPreview({ csvImport, onConfirm, onBack }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get(`/api/admin/imports/${csvImport.id}/preview`)
      .then(r => setPreview(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Erreur preview'))
      .finally(() => setLoading(false));
  }, [csvImport.id]);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      await api.post(`/api/admin/imports/${csvImport.id}/run`);
      onConfirm();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lancement import');
      setRunning(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: D.tx2 }}>Calcul du preview…</div>;
  }
  if (!preview) {
    return <div style={{ padding: '40px', textAlign: 'center', color: D.red }}>{error || 'Erreur'}</div>;
  }

  // Grouper entities par type
  const entitiesByType = {};
  (preview.entities_to_create || []).forEach(e => {
    if (!entitiesByType[e.type]) entitiesByType[e.type] = [];
    entitiesByType[e.type].push(e);
  });

  return (
    <div>
      {/* Récap */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: D.tx3, marginBottom: '4px' }}>Total lignes</div>
          <div style={{ fontSize: '22px', fontWeight: 600, color: D.tx }}>{preview.total_rows}</div>
        </div>
        <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: D.tx3, marginBottom: '4px' }}>À importer</div>
          <div style={{ fontSize: '22px', fontWeight: 600, color: D.green }}>
            {preview.total_rows - preview.duplicates_count}
          </div>
        </div>
        <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: D.tx3, marginBottom: '4px' }}>Doublons (skip)</div>
          <div style={{ fontSize: '22px', fontWeight: 600, color: preview.duplicates_count > 0 ? '#F0A843' : D.tx3 }}>
            {preview.duplicates_count}
          </div>
        </div>
      </div>

      {/* Entités à créer */}
      {preview.entities_to_create.length > 0 && (
        <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: D.or, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            À créer dans le catalogue
          </div>
          {Object.entries(entitiesByType).map(([type, items]) => (
            <div key={type} style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', color: D.tx3, marginBottom: '4px' }}>
                {type === 'greffe' && 'Greffes'}
                {type === 'formality_type' && 'Types de formalité'}
                {type === 'status_label' && 'Statuts'}
                {type === 'cabinet' && 'Cabinets'}
                {type === 'formaliste' && 'Formalistes'}
                {' '}({items.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {items.slice(0, 30).map((e, i) => (
                  <span key={i} style={{
                    padding: '3px 8px', borderRadius: '999px',
                    background: 'rgba(212,168,67,0.10)', color: D.or,
                    fontSize: '11px',
                  }}>{e.name} <span style={{ color: D.tx3 }}>({e.count})</span></span>
                ))}
                {items.length > 30 && (
                  <span style={{ fontSize: '11px', color: D.tx3, alignSelf: 'center' }}>+{items.length - 30}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sample rows */}
      {preview.sample_rows.length > 0 && (
        <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: D.tx, marginBottom: '10px' }}>
            Aperçu des 5 premières lignes
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${D.b1}` }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>#</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>Société</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>SIREN</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>Formalité</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>Statut</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>Greffe</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', color: D.tx3, fontWeight: 600 }}>Formaliste</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.map(r => (
                  <tr key={r.row_index} style={{ borderBottom: `0.5px solid ${D.b1}`, color: r.is_duplicate ? D.tx3 : D.tx }}>
                    <td style={{ padding: '6px 8px' }}>{r.row_index}{r.is_duplicate && <span title="Déjà importé" style={{ marginLeft: '4px', color: '#F0A843' }}>⚠</span>}</td>
                    <td style={{ padding: '6px 8px' }}>{r.societe_nom || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.societe_siren || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.formality_type || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.status_label || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.greffe || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.formaliste || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: '12px', padding: '10px 14px',
          background: 'rgba(239,91,91,0.10)', border: `1px solid ${D.red}40`,
          borderRadius: '6px', fontSize: '12px', color: D.red,
        }}>{error}</div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{
          background: 'transparent', color: D.tx2,
          border: `1px solid ${D.b1}`, padding: '10px 20px', borderRadius: '8px',
          fontSize: '13px', cursor: 'pointer',
        }}>← Retour mapping</button>
        <button
          onClick={handleRun}
          disabled={running}
          style={{
            background: D.green, color: D.bg,
            border: 'none', padding: '10px 24px', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {running ? 'Lancement...' : `Importer ${preview.total_rows - preview.duplicates_count} dossier${preview.total_rows - preview.duplicates_count > 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}

// ─── Étape 4 : Run + Done ────────────────────────────────────────────────────
function StepRunning({ csvImport, onDone, onRestart }) {
  const [job, setJob] = useState(csvImport);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/api/admin/imports/${csvImport.id}`);
        setJob(res.data);
        if (['completed', 'failed', 'partial'].includes(res.data.status)) {
          clearInterval(intervalRef.current);
        }
      } catch (e) { /* silent */ }
    }, 2000);
    return () => clearInterval(intervalRef.current);
  }, [csvImport.id]);

  const isDone = ['completed', 'partial', 'failed'].includes(job.status);
  const summary = job.summary || {};
  const errors = job.errors || [];

  return (
    <div>
      <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ fontSize: '13px', color: D.tx, fontWeight: 500 }}>
            {!isDone && 'Import en cours…'}
            {job.status === 'completed' && '✓ Import terminé'}
            {job.status === 'partial' && '⚠ Import partiel (avec erreurs)'}
            {job.status === 'failed' && '✗ Import échoué'}
          </div>
          <div style={{ fontSize: '12px', color: D.tx2 }}>
            {job.processed_rows} / {job.total_rows} lignes
          </div>
        </div>
        <div style={{ height: '6px', background: D.s2, borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{
            width: `${job.progress}%`, height: '100%',
            background: job.status === 'failed' ? D.red : job.status === 'partial' ? '#F0A843' : D.green,
            transition: 'width 0.3s',
          }} />
        </div>

        {/* Stats live */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 600, color: D.green }}>{job.success_count}</div>
            <div style={{ fontSize: '10px', color: D.tx3, textTransform: 'uppercase' }}>Importés</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 600, color: '#F0A843' }}>{job.skipped_count}</div>
            <div style={{ fontSize: '10px', color: D.tx3, textTransform: 'uppercase' }}>Skippés</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 600, color: job.error_count > 0 ? D.red : D.tx3 }}>
              {job.error_count}
            </div>
            <div style={{ fontSize: '10px', color: D.tx3, textTransform: 'uppercase' }}>Erreurs</div>
          </div>
        </div>
      </div>

      {/* Summary détaillé */}
      {isDone && Object.keys(summary).length > 0 && (
        <div style={{ background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: D.or, marginBottom: '10px', textTransform: 'uppercase' }}>Détail de l'import</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px', fontSize: '12px' }}>
            {summary.dossiers_created !== undefined && <div style={{ color: D.tx2 }}>Dossiers créés : <strong style={{ color: D.tx }}>{summary.dossiers_created}</strong></div>}
            {summary.dossiers_skipped !== undefined && summary.dossiers_skipped > 0 && <div style={{ color: D.tx2 }}>Dossiers skippés : <strong style={{ color: D.tx }}>{summary.dossiers_skipped}</strong></div>}
            {summary.greffes_created !== undefined && summary.greffes_created > 0 && <div style={{ color: D.tx2 }}>Greffes créés : <strong style={{ color: D.tx }}>{summary.greffes_created}</strong></div>}
            {summary.formality_types_created !== undefined && summary.formality_types_created > 0 && <div style={{ color: D.tx2 }}>Types créés : <strong style={{ color: D.tx }}>{summary.formality_types_created}</strong></div>}
            {summary.sub_types_created !== undefined && summary.sub_types_created > 0 && <div style={{ color: D.tx2 }}>Sous-types créés : <strong style={{ color: D.tx }}>{summary.sub_types_created}</strong></div>}
            {summary.status_labels_created !== undefined && summary.status_labels_created > 0 && <div style={{ color: D.tx2 }}>Statuts créés : <strong style={{ color: D.tx }}>{summary.status_labels_created}</strong></div>}
            {summary.cabinets_created !== undefined && summary.cabinets_created > 0 && <div style={{ color: D.tx2 }}>Cabinets créés : <strong style={{ color: D.tx }}>{summary.cabinets_created}</strong></div>}
            {summary.formalistes_created !== undefined && summary.formalistes_created > 0 && <div style={{ color: D.tx2 }}>Formalistes créés : <strong style={{ color: D.tx }}>{summary.formalistes_created}</strong></div>}
            {summary.attachments_queued !== undefined && summary.attachments_queued > 0 && <div style={{ color: D.tx2 }}>Pièces téléchargées : <strong style={{ color: D.tx }}>{summary.attachments_queued}</strong></div>}
          </div>
        </div>
      )}

      {/* Erreurs */}
      {errors.length > 0 && (
        <div style={{ background: 'rgba(239,91,91,0.06)', border: `1px solid ${D.red}30`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: D.red, marginBottom: '8px' }}>
            {errors.length} erreur{errors.length > 1 ? 's' : ''} pendant l'import
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {errors.slice(0, 20).map((e, i) => (
              <div key={i} style={{ fontSize: '11px', color: D.tx2, padding: '4px 0', borderBottom: `0.5px solid ${D.b1}` }}>
                <strong style={{ color: D.tx }}>Ligne {e.row}</strong>{e.data?.societe_nom && ` (${e.data.societe_nom})`}: {e.error}
              </div>
            ))}
            {errors.length > 20 && (
              <div style={{ fontSize: '11px', color: D.tx3, marginTop: '4px' }}>
                +{errors.length - 20} autres erreurs (consulter le job en DB)
              </div>
            )}
          </div>
        </div>
      )}

      {isDone && (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <button onClick={onRestart} style={{
            background: 'transparent', color: D.tx2,
            border: `1px solid ${D.b1}`, padding: '10px 20px', borderRadius: '8px',
            fontSize: '13px', cursor: 'pointer',
          }}>Nouvel import</button>
          <button onClick={onDone} style={{
            background: D.or, color: D.bg,
            border: 'none', padding: '10px 24px', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}>Voir les dossiers importés →</button>
        </div>
      )}
    </div>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────
export default function ImportsPage({ initialWorkspaceId, onClose, embedded = false, workspaceName = '' }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);  // 0: upload, 1: mapping, 2: preview, 3: running
  const [csvImport, setCsvImport] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    if (!embedded) {
      api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data || []));
    } else if (initialWorkspaceId) {
      // En embedded, on n'a besoin que du workspace courant pour le label
      setWorkspaces([{ id: initialWorkspaceId, name: workspaceName || 'Ce client' }]);
    }
  }, [embedded, initialWorkspaceId, workspaceName]);

  const handleRestart = () => {
    setCsvImport(null);
    setStep(0);
  };

  const handleDone = () => {
    if (embedded) {
      // En embedded, on reste dans la page client après import
      handleRestart();
      if (onClose) onClose();
    } else if (csvImport?.workspace_id) {
      window.location.href = `/admin?ws=${csvImport.workspace_id}#operations`;
    } else {
      handleRestart();
    }
  };

  const content = (
    <div style={{ maxWidth: embedded ? '100%' : '900px', margin: embedded ? '0' : '0 auto' }}>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '20px' }}>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '20px', fontWeight: 600 }}>
              Import CSV
            </div>
            <div style={{ fontSize: '12px', color: D.tx2, marginTop: '2px' }}>
              Importer des dossiers en masse depuis un fichier CSV
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none', color: D.tx2,
              fontSize: '20px', cursor: 'pointer',
            }}>×</button>
          )}
        </div>
      )}

      <StepIndicator current={step} />

      {step === 0 && (
        <StepUpload
          workspaceId={initialWorkspaceId}
          workspaces={workspaces}
          embedded={embedded}
          onUploaded={(data) => { setCsvImport(data); setStep(1); }}
        />
      )}
      {step === 1 && csvImport && (
        <StepMapping
          csvImport={csvImport}
          onMappingDone={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      )}
      {step === 2 && csvImport && (
        <StepPreview
          csvImport={csvImport}
          onConfirm={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && csvImport && (
        <StepRunning
          csvImport={csvImport}
          onDone={handleDone}
          onRestart={handleRestart}
        />
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div style={{ background: D.bg, minHeight: '100vh', padding: '24px 32px', color: D.tx }}>
      {content}
    </div>
  );
}
